import { useMemo } from 'react';
import Plot from 'react-plotly.js';
import type { Data, Layout } from 'plotly.js';
import type { TestChartData } from '@/lib/impedance';
import { radToDeg } from '@/lib/impedance';

/**
 * Appends truncated notes to a trace label for the chart legend.
 * Full notes appear in hovertemplate; legend shows at most 40 chars.
 */
export function formatLegendLabel(base: string, notes?: string): string {
  if (!notes) return base;
  const truncated = notes.length > 40 ? `${notes.slice(0, 37)}...` : notes;
  return `${base} — ${truncated}`;
}

export interface BodePlotProps {
  testData: TestChartData[];
  viewMode: 'baselines' | 'specimens' | 'both';
  visibleChannels: Set<string>;
  phaseUnit: 'rad' | 'deg';
  yScale: 'linear' | 'log';
  colorMap: Map<string, string>;
  onPlotRef?: (el: HTMLElement | null) => void;
}

export function BodePlot({
  testData,
  viewMode,
  visibleChannels,
  phaseUnit,
  yScale,
  colorMap,
  onPlotRef,
}: BodePlotProps) {
  const traces = useMemo<Data[]>(() => {
    const result: Data[] = [];
    const useWebGL = testData.length > 10;
    const traceType = useWebGL ? 'scattergl' : 'scatter';

    for (const test of testData) {
      const color = colorMap.get(test.testId) ?? '#888';

      const measurementTypes: ('baseline' | 'specimen')[] =
        viewMode === 'baselines'
          ? ['baseline']
          : viewMode === 'specimens'
            ? ['specimen']
            : ['baseline', 'specimen'];

      for (const mtype of measurementTypes) {
        const measurementData = mtype === 'baseline' ? test.baseline : test.specimen;
        if (!measurementData) continue;

        for (const channel of measurementData.channels) {
          if (!visibleChannels.has(channel.channelId)) continue;

          const baseLabel = `${test.label} ${channel.channelId} ${mtype === 'baseline' ? 'BL' : 'SP'}`;
          const label = formatLegendLabel(baseLabel, test.notes);
          const notesLine = test.notes ? `Note: ${test.notes}<br>` : '';

          // Magnitude trace (yaxis: y, top panel → xaxis2)
          result.push({
            x: channel.points.map((p) => p.frequency),
            y: channel.points.map((p) => p.magnitude),
            type: traceType as 'scatter' | 'scattergl',
            mode: 'lines+markers',
            name: label,
            xaxis: 'x2',
            yaxis: 'y',
            line: {
              color,
              dash: mtype === 'baseline' ? 'solid' : 'dash',
              width: 1.5,
            },
            marker: { size: 3 },
            legendgroup: test.testId,
            legendgrouptitle: result.find((t) => (t as any).legendgroup === test.testId)
              ? undefined
              : { text: test.label },
            hovertemplate:
              `<b>${baseLabel}</b><br>` +
              notesLine +
              `Freq: %{x:.2f} Hz<br>` +
              `Mag: %{y:.1f} Ω<br>` +
              `<extra></extra>`,
            showlegend: true,
          } as Data);

          // Phase trace (yaxis: y2)
          const phaseValues =
            phaseUnit === 'deg'
              ? channel.points.map((p) => radToDeg(p.phase))
              : channel.points.map((p) => p.phase);

          result.push({
            x: channel.points.map((p) => p.frequency),
            y: phaseValues,
            type: traceType as 'scatter' | 'scattergl',
            mode: 'lines',
            name: `${label} φ`,
            yaxis: 'y2',
            line: {
              color,
              dash: mtype === 'baseline' ? 'dot' : 'dashdot',
              width: 1,
            },
            legendgroup: test.testId,
            hovertemplate:
              `<b>${baseLabel} phase</b><br>` +
              notesLine +
              `Freq: %{x:.2f} Hz<br>` +
              `Phase: %{y:.3f} ${phaseUnit === 'deg' ? '°' : 'rad'}<br>` +
              `<extra></extra>`,
            showlegend: false,
          } as Data);
        }
      }
    }

    return result;
  }, [testData, viewMode, visibleChannels, phaseUnit, colorMap]);

  const layout = useMemo<Partial<Layout>>(
    () => ({
      // Stacked subplots: xaxis2 (top/magnitude, no labels) linked to xaxis (bottom/phase, master)
      xaxis: {
        title: { text: 'Frequency (Hz)' },
        type: 'log',
        autorange: true,
        anchor: 'y2',
      },
      xaxis2: {
        type: 'log',
        matches: 'x',
        showticklabels: false,
        anchor: 'y',
      } as Partial<Layout>['xaxis'],
      yaxis: {
        title: { text: 'Magnitude (Ω)' },
        type: yScale,
        autorange: true,
        domain: [0.55, 1.0],
        anchor: 'x2',
      },
      yaxis2: {
        title: { text: `Phase (${phaseUnit === 'deg' ? '°' : 'rad'})` },
        autorange: true,
        domain: [0.0, 0.45],
        anchor: 'x',
      },
      legend: {
        orientation: 'h',
        y: -0.22,
        x: 0,
        groupclick: 'toggleitem',
      },
      hovermode: 'closest',
      margin: { t: 30, r: 20, b: 120, l: 60 },
      autosize: true,
    }),
    [yScale, phaseUnit],
  );

  if (testData.length === 0) {
    return null;
  }

  return (
    <Plot
      data={traces}
      layout={layout}
      style={{ width: '100%', height: '100%' }}
      useResizeHandler
      config={{ responsive: true, displayModeBar: true, displaylogo: false, scrollZoom: true }}
      onInitialized={(_, graphDiv) => onPlotRef?.(graphDiv)}
      onPurge={() => onPlotRef?.(null)}
    />
  );
}
